<script lang="ts">
  import type { User } from "firebase/auth";
  import { onDestroy, onMount } from "svelte";
  import { push } from "svelte-spa-router";
  import { Alert, Button, Col, Container, Row, Spinner } from "sveltestrap";
  import GithubButton from "../components/GithubButton.svelte";
  import OrganizationList from "../components/OrganizationList.svelte";
  import OrgApiKeyField from "../components/OrgApiKeyField.svelte";
  import { signOut } from "../helpers/auth";
  import type { OrganizationInfo } from "../helpers/orgTypes";
  import { getOrganizations, FunctionResult } from "../helpers/requests";
  import { auth, githubAppUrl, orgStore } from "../stores";

  let orgs: OrganizationInfo[];
  let user: User;
  let selectedOrg: OrganizationInfo | null = null;
  let listOrganizationsPromise: Promise<FunctionResult>;

  function onOrgChanged(event: CustomEvent) {
    selectedOrg = event.detail.org;
  }

  const organizationSubscription = orgStore.subscribe(
    (organizations) => (orgs = organizations as OrganizationInfo[])
  );

  onMount(async () => {
    return auth.subscribe(async (u) => {
      user = u as User;
      if (u) {
        listOrganizationsPromise = new Promise(async (resolve, reject) => {
          const listOrganizations = await getOrganizations();
          if (listOrganizations.code === 401) {
            await signOut();
            await push("/");
            reject();
          } else if (listOrganizations.code === 200) {
            const downloadedOrgs =
              listOrganizations.message as OrganizationInfo[];
            selectedOrg = downloadedOrgs[0];
            orgStore.set(downloadedOrgs);
            resolve(listOrganizations);
          } else if (listOrganizations.code === 403) {
            resolve(listOrganizations);
          }
        });
      }
    });
  });

  onDestroy(organizationSubscription);
</script>

<div class="dashboard">
  <Container>
    {#if !user}
      <Alert color="secondary">
        <h4 class="alert-heading">No user detected</h4>
        For some reason you are not logged in and we can not show you this page unless
        you actually log in.
        <hr />
        <Button on:click={auth.signIn}>Log in</Button>
      </Alert>
    {:else}
      {#await listOrganizationsPromise}
        <Alert color="success">
          <h4 class="alert-heading text-capitalize">Loading</h4>
          We are currently fetching the organizations that you have access. Please,
          wait for a moment. In the meanwhile, you can look at this spinner spin:
          <hr />
          <Spinner color="dark" />
        </Alert>
      {:then}
        {#if orgs}
          <Row>
            <Col sm="3">
              <OrganizationList orgInfo={orgs} on:orgSelected={onOrgChanged} />
            </Col>
            <Col sm="9">
              <OrgApiKeyField org={selectedOrg} />
            </Col>
          </Row>
        {:else}
          <Alert color="warning">
            <h4 class="alert-heading text-capitalize">
              Missing App Installation
            </h4>
            You need to install the GitHub app to see the linked organizations. Please
            do so here:
            <hr />
            <GithubButton githubUrl={$githubAppUrl} />
          </Alert>
        {/if}
        {:catch error}
        <Alert color="warning">
            <h4 class="alert-heading text-capitalize">
              We had an error
            </h4>
            I'm afraid we had the following error:<br/>
            {error.message}
          </Alert>
      {/await}
    {/if}
  </Container>
</div>

<style>
  .dashboard {
    padding-top: 6vh;
  }
</style>
